import React, { useState, useRef, useCallback } from 'react';
import { Checkbox } from '../../../components/ui/Checkbox';

import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

const PhysicalConditionGrid = ({ conditionData, onChange, errors }) => {
  const [expandedZone, setExpandedZone] = useState(null);
  const [uploadingZone, setUploadingZone] = useState(null);
  const [uploadError, setUploadError] = useState({});
  const fileInputRefs = useRef({});

  const bodyZones = [
    {
      id: 'front',
      label: 'Frente',
      icon: 'ArrowUp',
      damageTypes: ['rayones', 'golpes', 'pintura', 'cristales']
    },
    {
      id: 'rear',
      label: 'Parte Trasera',
      icon: 'ArrowDown',
      damageTypes: ['rayones', 'golpes', 'pintura', 'cristales']
    },
    {
      id: 'leftSide',
      label: 'Lado Izquierdo',
      icon: 'ArrowLeft',
      damageTypes: ['rayones', 'golpes', 'pintura', 'cristales', 'espejos']
    },
    {
      id: 'rightSide',
      label: 'Lado Derecho',
      icon: 'ArrowRight',
      damageTypes: ['rayones', 'golpes', 'pintura', 'cristales', 'espejos']
    },
    {
      id: 'roof',
      label: 'Techo',
      icon: 'Square',
      damageTypes: ['rayones', 'golpes', 'pintura']
    },
    {
      id: 'interior',
      label: 'Interior',
      icon: 'Home',
      damageTypes: ['tapiceria', 'tablero', 'volante', 'asientos', 'alfombras']
    }
  ];

  const damageLabels = {
    rayones: 'Rayones',
    golpes: 'Golpes',
    pintura: 'Daño en Pintura',
    cristales: 'Cristales Dañados',
    espejos: 'Espejos Dañados',
    tapiceria: 'Tapicería Dañada',
    tablero: 'Tablero Dañado',
    volante: 'Volante Dañado',
    asientos: 'Asientos Dañados',
    alfombras: 'Alfombras Dañadas'
  };

  const handleDamageToggle = (zoneId, damageType) => {
    const updatedCondition = { ...conditionData };
    if (!updatedCondition?.[zoneId]) {
      updatedCondition[zoneId] = { damages: [], photos: [] };
    }
    const damages = [...(updatedCondition?.[zoneId]?.damages || [])];
    const index = damages?.indexOf(damageType);
    if (index > -1) {
      damages?.splice(index, 1);
    } else {
      damages?.push(damageType);
    }
    updatedCondition[zoneId] = { ...updatedCondition?.[zoneId], damages };
    onChange(updatedCondition);
  };

  // Upload a single file to Supabase storage, returns { url, path, name } or null on error
  const uploadFileToStorage = async (file, zoneId) => {
    try {
      const timestamp = Date.now();
      const randomStr = Math.random()?.toString(36)?.substring(7);
      const fileExt = file?.name?.split('.')?.pop();
      const filePath = `reception/${zoneId}/${timestamp}-${randomStr}.${fileExt}`;

      const { data, error } = await supabase?.storage?.from('vehicle-condition-photos')?.upload(filePath, file, { cacheControl: '3600', upsert: false });

      if (error) {
        console.error('[PHOTO_UPLOAD] Storage error:', error);
        return null;
      }

      // Get a signed URL for preview
      const { data: signedData } = await supabase?.storage?.from('vehicle-condition-photos')?.createSignedUrl(data?.path, 3600);

      return {
        url: signedData?.signedUrl || URL.createObjectURL(file),
        path: data?.path,
        name: file?.name,
        localUrl: URL.createObjectURL(file)
      };
    } catch (err) {
      console.error('[PHOTO_UPLOAD] Exception:', err);
      return null;
    }
  };

  const handlePhotoSelect = useCallback(async (zoneId, event) => {
    const files = Array.from(event?.target?.files || []);
    if (!files?.length) return;

    // Reset input so same file can be re-selected
    if (event?.target) event.target.value = '';

    setUploadingZone(zoneId);
    setUploadError(prev => ({ ...prev, [zoneId]: null }));

    const uploadedPhotos = [];
    const failedCount = { count: 0 };

    for (const file of files) {
      // Immediately show local preview
      const localPreview = {
        url: URL.createObjectURL(file),
        path: null,
        name: file?.name,
        uploading: true
      };

      // Add local preview immediately
      onChange(prev => {
        const updated = { ...prev };
        if (!updated?.[zoneId]) updated[zoneId] = { damages: [], photos: [] };
        updated[zoneId] = {
          ...updated?.[zoneId],
          photos: [...(updated?.[zoneId]?.photos || []), localPreview]
        };
        return updated;
      });

      // Upload to storage
      const result = await uploadFileToStorage(file, zoneId);

      if (result) {
        uploadedPhotos?.push(result);
        // Replace local preview with uploaded version
        onChange(prev => {
          const updated = { ...prev };
          if (!updated?.[zoneId]) return updated;
          const photos = [...(updated?.[zoneId]?.photos || [])];
          // Find the uploading placeholder for this file
          const idx = photos?.findIndex(p => p?.name === file?.name && p?.uploading === true);
          if (idx > -1) {
            photos[idx] = { ...result, uploading: false };
          }
          updated[zoneId] = { ...updated?.[zoneId], photos };
          return updated;
        });
      } else {
        failedCount.count++;
        // Remove the failed placeholder
        onChange(prev => {
          const updated = { ...prev };
          if (!updated?.[zoneId]) return updated;
          const photos = (updated?.[zoneId]?.photos || [])?.filter(
            p => !(p?.name === file?.name && p?.uploading === true)
          );
          updated[zoneId] = { ...updated?.[zoneId], photos };
          return updated;
        });
      }
    }

    setUploadingZone(null);

    if (failedCount?.count > 0) {
      setUploadError(prev => ({
        ...prev,
        [zoneId]: `${failedCount?.count} foto(s) no se pudieron subir. Verifica tu conexión e intenta de nuevo.`
      }));
    }
  }, [onChange]);

  const triggerFileInput = (zoneId, useCamera = false) => {
    const input = fileInputRefs?.current?.[zoneId];
    if (!input) return;
    // Set capture attribute for camera on mobile
    if (useCamera) {
      input?.setAttribute('capture', 'environment');
    } else {
      input?.removeAttribute('capture');
    }
    input?.click();
  };

  const handlePhotoRemove = (zoneId, photoIndex) => {
    const updatedCondition = { ...conditionData };
    const photo = updatedCondition?.[zoneId]?.photos?.[photoIndex];
    // Revoke local object URL to prevent memory leaks
    if (photo?.localUrl) URL.revokeObjectURL(photo?.localUrl);
    else if (photo?.url && photo?.url?.startsWith('blob:')) URL.revokeObjectURL(photo?.url);
    const photos = [...(updatedCondition?.[zoneId]?.photos || [])];
    photos?.splice(photoIndex, 1);
    updatedCondition[zoneId] = { ...updatedCondition?.[zoneId], photos };
    onChange(updatedCondition);
  };

  const hasDamages = (zoneId) => conditionData?.[zoneId]?.damages?.length > 0;
  const hasPhotos = (zoneId) => conditionData?.[zoneId]?.photos?.length > 0;

  return (
    <div className="bg-card rounded-lg border border-border shadow-elevation-2 p-4 md:p-6">
      <div className="flex items-center gap-2 mb-4 md:mb-6">
        <div className="p-2 rounded-lg bg-warning/10">
          <Icon name="AlertTriangle" size={24} className="text-warning" />
        </div>
        <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground">
          Estado Físico del Vehículo
        </h2>
      </div>

      {errors?.physicalCondition && (
        <div className="mb-4 flex items-center gap-2 p-3 rounded-lg bg-error/10 border border-error/20">
          <Icon name="AlertCircle" size={20} className="text-error" />
          <p className="text-sm caption text-error">{errors?.physicalCondition}</p>
        </div>
      )}

      <div className="space-y-4">
        {bodyZones?.map((zone) => {
          const isExpanded = expandedZone === zone?.id;
          const zoneHasDamages = hasDamages(zone?.id);
          const zoneHasPhotos = hasPhotos(zone?.id);
          const isUploading = uploadingZone === zone?.id;
          const zoneUploadError = uploadError?.[zone?.id];

          return (
            <div
              key={zone?.id}
              className={`border rounded-lg transition-smooth ${
                zoneHasDamages ? 'border-warning bg-warning/5' : 'border-border bg-muted/30'
              }`}
            >
              <button
                type="button"
                onClick={() => setExpandedZone(isExpanded ? null : zone?.id)}
                className="w-full flex items-center justify-between p-4 hover:bg-muted/50 transition-smooth"
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${zoneHasDamages ? 'bg-warning/20' : 'bg-primary/10'}`}>
                    <Icon name={zone?.icon} size={20} className={zoneHasDamages ? 'text-warning' : 'text-primary'} />
                  </div>
                  <div className="text-left">
                    <h3 className="text-base font-heading font-semibold text-foreground">{zone?.label}</h3>
                    {zoneHasDamages && (
                      <p className="text-sm caption text-warning">
                        {conditionData?.[zone?.id]?.damages?.length} daño(s) reportado(s)
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {zoneHasPhotos && (
                    <span className="flex items-center gap-1 text-sm caption text-muted-foreground">
                      <Icon name="Camera" size={16} />
                      {conditionData?.[zone?.id]?.photos?.length}
                    </span>
                  )}
                  <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={20} className="text-muted-foreground" />
                </div>
              </button>

              {isExpanded && (
                <div className="p-4 border-t border-border space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {zone?.damageTypes?.map((damageType) => (
                      <Checkbox
                        key={damageType}
                        label={damageLabels?.[damageType]}
                        checked={conditionData?.[zone?.id]?.damages?.includes(damageType) || false}
                        onChange={() => handleDamageToggle(zone?.id, damageType)}
                      />
                    ))}
                  </div>

                  {/* Evidence photo section — always visible when zone is expanded */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <label className="text-sm font-body font-medium text-foreground">
                        Evidencia Fotográfica
                        {zoneHasPhotos && (
                          <span className="ml-1 text-muted-foreground">({conditionData?.[zone?.id]?.photos?.length})</span>
                        )}
                      </label>

                      {/* Hidden file input — NOT inside a label */}
                      <input
                        ref={el => { fileInputRefs.current[zone?.id] = el; }}
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        onChange={(e) => handlePhotoSelect(zone?.id, e)}
                      />

                      <div className="flex items-center gap-2">
                        {/* Camera button — triggers camera on mobile, file picker on desktop */}
                        <button
                          type="button"
                          disabled={isUploading}
                          onClick={() => triggerFileInput(zone?.id, true)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md border border-input hover:bg-accent hover:text-accent-foreground transition-colors disabled:opacity-50 disabled:pointer-events-none"
                        >
                          {isUploading ? (
                            <><div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" /><span>Subiendo...</span></>
                          ) : (
                            <><Icon name="Camera" size={14} /><span>Tomar Foto</span></>
                          )}
                        </button>

                        {/* Gallery/file picker button */}
                        <button
                          type="button"
                          disabled={isUploading}
                          onClick={() => triggerFileInput(zone?.id, false)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md border border-input hover:bg-accent hover:text-accent-foreground transition-colors disabled:opacity-50 disabled:pointer-events-none"
                        >
                          <Icon name="ImagePlus" size={14} />
                          <span>Galería</span>
                        </button>
                      </div>
                    </div>

                    {/* Upload error */}
                    {zoneUploadError && (
                      <div className="flex items-start gap-2 p-3 rounded-lg bg-error/10 border border-error/20">
                        <Icon name="AlertCircle" size={16} className="text-error mt-0.5 shrink-0" />
                        <div className="flex-1">
                          <p className="text-sm text-error">{zoneUploadError}</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setUploadError(prev => ({ ...prev, [zone?.id]: null }))}
                          className="text-error/60 hover:text-error"
                        >
                          <Icon name="X" size={14} />
                        </button>
                      </div>
                    )}

                    {/* Photo thumbnails */}
                    {zoneHasPhotos && (
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                        {conditionData?.[zone?.id]?.photos?.map((photo, index) => (
                          <div key={index} className="relative group">
                            <div className="aspect-square rounded-lg overflow-hidden bg-muted">
                              <Image
                                src={photo?.url || photo?.localUrl}
                                alt={`Evidencia fotográfica de ${zone?.label}`}
                                className="w-full h-full object-cover"
                              />
                              {photo?.uploading && (
                                <div className="absolute inset-0 bg-black/40 flex items-center justify-center rounded-lg">
                                  <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                </div>
                              )}
                            </div>
                            {!photo?.uploading && (
                              <button
                                type="button"
                                onClick={() => handlePhotoRemove(zone?.id, index)}
                                className="absolute top-2 right-2 p-1.5 rounded-full bg-error text-error-foreground opacity-0 group-hover:opacity-100 transition-smooth press-effect"
                              >
                                <Icon name="X" size={16} />
                              </button>
                            )}
                            {photo?.path && (
                              <div className="absolute bottom-2 left-2">
                                <Icon name="CheckCircle2" size={14} className="text-success drop-shadow" />
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {!zoneHasPhotos && zoneHasDamages && (
                      <div className="flex items-center gap-2 p-3 rounded-lg bg-warning/10 border border-warning/20">
                        <Icon name="AlertCircle" size={16} className="text-warning" />
                        <p className="text-sm caption text-warning">
                          Recomendado: agrega evidencia fotográfica para los daños reportados
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PhysicalConditionGrid;